import React, { useEffect, useRef, useState, forwardRef } from 'react'
import { format } from 'date-fns'
import { CaptionProps, DateAfter, DateBefore, DayPicker, DayPickerSingleProps, useNavigation } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { tv, type VariantProps } from 'tailwind-variants'
import IconUse from '_/ui/icons/use'
import { Label } from './label'
import { SimpleSelect } from './simple-select'
import dayjs from 'dayjs'

export const input = tv({
	base: 'text-base font-normal rounded border px-6 py-3 pr-20 w-full focus:ring-2 focus:ring-primary-neutral cursor-pointer group',
	variants: {
		themeColor: {
			default: 'text-gray-200 border-primary-light hover:border-gray-700',
			invalid: 'text-red-500 border-red-500',
			success: 'text-primary-neutral border-primary-neutral',
		},
	},
	defaultVariants: {
		themeColor: 'default',
	},
})

export const wrapper = tv({
	base: 'relative w-full items-center',
	variants: {
		blocked: {
			true: 'opacity-50 pointer-events-none',
		},
	},
})

export const dropdown = tv({
	base: 'absolute top-full w-full px-3 py-4 left-0 mt-2 bg-gray-100 border border-primary-light rounded-md shadow-sm z-10 h-62 overflow-hidden max-lg:overflow-x-auto',
})

export const dropdownIcon = tv({
	base: 'absolute right-3 top-3 pointer-events-none ease-[cubic-bezier(0.87,_0,_0.13,_1)] transition-transform duration-300 data-[state=true]:rotate-180',
})

interface DatePickerProps extends VariantProps<typeof input> {
	id?: string
	name: string
	className?: string
	defaultValue?: Date
	onInputChange?: (inputName: string, newValue: Date) => void
	placeholder?: string
	flexibleOption?: boolean
	fromDate?: Date | null
	required?: boolean
	label?: string
	blocked?: boolean
	type?: string
	availableDates?: Date[] | null
	disabledDays?: Date[] | Date | DateAfter | DateBefore | null
	dateFormat?: string
	theme?: 'cruises' | 'theme-parks' | 'beach-resorts'
	hasYearAndMonthSelector?: boolean
	hasInitialDate?: boolean
}

const DatePickerComponent = forwardRef<HTMLInputElement, DatePickerProps>(
	(
		{
			id,
			name,
			className,
			blocked,
			themeColor,
			defaultValue,
			onInputChange,
			placeholder,
			fromDate = new Date(),
			required,
			label,
			availableDates,
			theme,
			disabledDays,
			dateFormat = 'PPP',
			hasYearAndMonthSelector = false,
			hasInitialDate = true,
		},
		ref,
	) => {
		const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
		const [selectedDay, setSelectedDay] = useState<Date | null>(defaultValue ? new Date(defaultValue) : null)
		const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)
		const dropdownRef = useRef<HTMLDivElement | null>(null)

		useEffect(() => {
			if (isInitialLoad && defaultValue) {
				setSelectedDay(new Date(defaultValue))
				setIsInitialLoad(false)
			}
		}, [defaultValue, isInitialLoad])

		const isDayEnabled = (day: Date) => {
			if (!availableDates) {
				return false
			}
			const inputDateUTC = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()))
			return availableDates.some(enabledDay => {
				const enabledDateUTC = new Date(enabledDay)
				return (
					inputDateUTC.getUTCFullYear() === enabledDateUTC.getUTCFullYear() &&
					inputDateUTC.getUTCMonth() === enabledDateUTC.getUTCMonth() &&
					inputDateUTC.getUTCDate() === enabledDateUTC.getUTCDate()
				)
			})
		}

		const CustomCaption = (props: CaptionProps) => {
			const { goToMonth, nextMonth, previousMonth } = useNavigation()
			return (
				<div className='flex justify-between items-center mb-3'>
					<button type='button' disabled={!previousMonth} onClick={() => previousMonth && goToMonth(previousMonth)}>
						<IconUse id='chevron-left' className='text-primary-neutral' width={24} height={24} />
					</button>
					<h4>{format(props.displayMonth, 'MMMM yyy')}</h4>
					<button type='button' disabled={!nextMonth} onClick={() => nextMonth && goToMonth(nextMonth)}>
						<IconUse id='chevron-right' className='text-primary-neutral' width={24} height={24} />
					</button>
				</div>
			)
		}

		const months = Array.from({ length: 12 }, (_, i) => format(new Date(0, i), 'MMMM'))
		const currentYear = new Date().getFullYear()
		const extendedYear = currentYear + 30

		const years = Array.from(
			{ length: (name === 'expiration_date' ? extendedYear : currentYear) - 1920 + 1 },
			(_, i) => 1920 + i,
		).reverse()

		const CustomCaptionWithMonthAndYearSelect = (props: CaptionProps) => {
			const { goToMonth, nextMonth, previousMonth } = useNavigation()

			const handleMonthChange = (month: string | number) => {
				const newMonth = new Date(props.displayMonth)
				newMonth.setMonth(Number(month))
				goToMonth(newMonth)
			}

			const handleYearChange = (year: string | number) => {
				const newMonth = new Date(props.displayMonth)
				newMonth.setFullYear(Number(year))
				goToMonth(newMonth)
			}

			return (
				<div className='flex justify-between items-center mb-3'>
					<button type='button' disabled={!previousMonth} onClick={() => previousMonth && goToMonth(previousMonth)}>
						<IconUse id='chevron-left' className='text-primary-neutral' width={24} height={24} />
					</button>
					<div className='relative flex items-center space-x-2 w-[72%]'>
						<SimpleSelect
							options={months.map((month, index) => ({ value: index, label: month }))}
							value={props.displayMonth.getMonth()}
							onChange={handleMonthChange}
							className='lg:relative max-h-10 text-center w-1/2'
						/>
						<SimpleSelect
							options={years.map(year => ({ value: year, label: String(year) }))}
							value={props.displayMonth.getFullYear()}
							onChange={handleYearChange}
							className='lg:relative max-h-10 text-center w-1/2'
						/>
					</div>
					<button type='button' disabled={!nextMonth} onClick={() => nextMonth && goToMonth(nextMonth)}>
						<IconUse id='chevron-right' className='text-primary-neutral' width={24} height={24} />
					</button>
				</div>
			)
		}

		const closeDropdown = () => {
			setIsDropdownOpen(false)
		}

		const clearValues = () => {
			setSelectedDay(null)
			closeDropdown()
			if (onInputChange) {
				onInputChange(name, new Date('invalid'))
			}
		}

		const handleDayClick = (day: Date) => {
			setSelectedDay(day)
			closeDropdown()
			if (onInputChange) {
				onInputChange(name, day)
			}
		}

		const handleInputClick = () => {
			setIsDropdownOpen(state => !state)
		}

		useEffect(() => {
			function handleClickOutside(event: MouseEvent) {
				if (
					dropdownRef.current &&
					!(dropdownRef.current as any).contains(event.target) &&
					(event.target as HTMLElement).nodeName.toLowerCase() !== 'input'
				) {
					closeDropdown()
				}
			}

			document.addEventListener('mousedown', handleClickOutside)
			return () => {
				document.removeEventListener('mousedown', handleClickOutside)
			}
		}, [])

		let value = ''
		if (selectedDay && !hasInitialDate) {
			value = format(new Date(selectedDay), dateFormat)
		}

		const currentDate = new Date();
		const isSameDate = dayjs(selectedDay).isSame(dayjs(currentDate), 'day');

		if (hasInitialDate && selectedDay && !isSameDate) {
			value = format(new Date(selectedDay), dateFormat)
		}

		const dayPickerProps: Partial<DayPickerSingleProps> & { mode: 'single' } = {
			mode: 'single',
			selected: selectedDay ? (selectedDay as Date) : undefined,
			onDayClick: handleDayClick,
			classNames: {
				head: 'text-gray-200 capitalize',
				table: 'w-full',
				month: 'w-full',
				cell: '',
				day_today: hasInitialDate ? '!bg-primary-neutral/10 !font-bold' : '',
			},
			fromDate: fromDate ? fromDate : undefined,
			captionLayout: 'dropdown-buttons',
			components: {
				Caption: hasYearAndMonthSelector ? CustomCaptionWithMonthAndYearSelect : CustomCaption,
			},
			disabled: disabledDays ? disabledDays : undefined,
		}

		if (theme === 'cruises') {
			Object.assign(dayPickerProps, { disabled: (day: Date) => !isDayEnabled(day) })
		}

		return (
			<>
				{label && <Label htmlFor={name}>{label}</Label>}
				<div className={wrapper({ class: className, blocked })}>
					<input
						id={id ? id : name}
						name={name}
						className={input({ class: className, themeColor })}
						value={value}
						onClick={handleInputClick}
						placeholder={placeholder && placeholder}
						required={required}
						readOnly
						ref={ref}
					/>
					<IconUse
						id='chevron-down'
						width={24}
						height={24}
						data-state={isDropdownOpen}
						className={dropdownIcon({ class: className })}
					/>
					{selectedDay && (
						<IconUse
							id='close'
							width={15}
							height={15}
							className='absolute text-primary-neutral right-12 top-4 cursor-pointer'
							onClick={() => {
								clearValues()
								closeDropdown()
							}}
						/>
					)}
					{isDropdownOpen && (
						<div className={dropdown()} ref={dropdownRef}>
							<DayPicker {...dayPickerProps} />
						</div>
					)}
				</div>
			</>
		)
	},
)

DatePickerComponent.displayName = 'DatePickerComponent'

export const DatePicker = DatePickerComponent
